import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Button,
	Col,
	Form,
	Input,
	Modal,
	Row,
	Select,
	message,
	notification,
} from "antd";
import { FiArrowLeft, FiUser } from "react-icons/fi";
import dayjs from "dayjs";
import config from "../../config";
import serviceProfile from "../../services/profile";
import { removeStorageWhenLogout } from "../../config/helper";

const ModalEditProfile = ({
	openModal,
	onClose = () => {},
	profileData = {},
}) => {
	const formRef = useRef();
	const navigate = useNavigate();

	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [initState, setInitState] = useState({
		name: null,
		pronouns: null,
		gender: null,
		dobDay: "01",
		dobMonth: "Jan",
		dobYear: new Date().getFullYear(),
	});

	const handleSaveProfile = async (values) => {
		setLoading(true);

		const gender = handleGetGender(values.pronouns);

		const payload = {
			data: JSON.stringify({
				name: values.name,
				pronouns: values.pronouns,
				gender: gender,
				dob: `${dayjs(
					`${values.dobDay}-${values.dobMonth}-${values.dobYear}`
				).format("YYYY-MM-DD")}T00:00:00Z`,
			}),
		};

		const response = await serviceProfile.updateProfile(payload);

		if (response.httpStatus === 200) {
			notification.success({ message: "Data has been saved." });
			onClose({
				popupShow: true,
				profileShow: false,
			});
			setTimeout(() => {
				navigate(0);
			}, config.timeout);
		}
		if (response.httpStatus === 401) {
			notification.error({ message: "Unauthorize!" });
			removeStorageWhenLogout();
			setTimeout(() => {
				navigate("/signin");
			}, config.timeout);
		}
		if (response.httpStatus === 500) {
			notification.error({
				message: "Something wrong, please try again in few minutes.",
			});
			setLoading(false);
		}
	};

	const handleGetGender = (value) => {
		const genderMap = {
			"He/Him": "male",
			"She/Her": "female",
			"They/Them": "other",
		};
		return genderMap[value] || null;
	};

	const dayOptions = Array.from({ length: 31 }, (_, i) => {
		const day = String(i + 1).padStart(2, "0");
		return { value: day, label: day };
	});

	const monthOptions = Array.from({ length: 12 }, (_, i) => {
		const month = new Date(0, i).toLocaleString("en-US", {
			month: "short",
		});
		return { value: month, label: month };
	});

	const yearOptions = (startYear = 1900) => {
		const currentYear = dayjs().subtract(18, "years").year();
		return Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
			const year = startYear + i;
			return { value: `${year}`, label: `${year}` };
		});
	};

	const fieldRules = [
		{
			required: true,
			message: "This field cannot empty!",
		},
	];

	useEffect(() => {
		setInitState(profileData);
	}, [profileData]);

	return (
		<Modal
			footer={null}
			className="profile-modal"
			open={openModal}
			onCancel={() => {
				onClose({ popupShow: true, profileShow: false });
			}}
			destroyOnClose
		>
			<div className="top-section">
				<div className="user-profile">
					<Button
						className="user-profile-icon"
						icon={<FiArrowLeft size={24} />}
						onClick={() => {
							onClose({
								popupShow: true,
								profileShow: false,
							});
						}}
					/>
					<div className="user-profile-wrapper">
						<span>Edit your profile</span>
						<Button
							type="link"
							loading={loading}
							onClick={() => formRef.current.submit()}
						>
							Save
						</Button>
					</div>
				</div>
				<Form
					autoComplete="off"
					ref={formRef}
					form={form}
					initialValues={initState}
					onFinish={handleSaveProfile}
				>
					<Form.Item name="name" rules={fieldRules}>
						<Input
							placeholder="Enter your name"
							prefix={<FiUser />}
						/>
					</Form.Item>
					<Form.Item name="pronouns" rules={fieldRules}>
						<Select
							placeholder="Enter your pronouns"
							options={[
								{ value: "He/Him", label: "He/Him" },
								{ value: "She/Her", label: "She/Her" },
								{
									value: "They/Them",
									label: "They/Them",
								},
							]}
						/>
					</Form.Item>
					<Form.Item>
						<Row gutter={10}>
							<Col xs={8}>
								<Form.Item name="dobDay" rules={fieldRules}>
									<Select
										showSearch
										suffixIcon={<></>}
										options={dayOptions}
									/>
								</Form.Item>
							</Col>
							<Col xs={8}>
								<Form.Item name="dobMonth" rules={fieldRules}>
									<Select
										showSearch
										suffixIcon={<></>}
										options={monthOptions}
									/>
								</Form.Item>
							</Col>
							<Col xs={8}>
								<Form.Item name="dobYear" rules={fieldRules}>
									<Select
										showSearch
										suffixIcon={<></>}
										options={yearOptions(1900)}
									/>
								</Form.Item>
							</Col>
						</Row>
					</Form.Item>
				</Form>
			</div>
		</Modal>
	);
};

export default ModalEditProfile;
